import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  LayoutDashboard,
  Users,
  ListChecks,
  GitBranch,
  Receipt,
  Building2,
  FileText,
  MessageSquare,
  Settings as SettingsIcon,
  Search,
  Bell,
  LogOut,
  Square,
  Sun,
  Moon,
  Menu,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme, useTenant } from "@/context/ThemeContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/clients", label: "Clients", icon: Users, testid: "nav-clients" },
  { to: "/tasks", label: "Tasks", icon: ListChecks, testid: "nav-tasks" },
  { to: "/workflow", label: "Workflow", icon: GitBranch, testid: "nav-workflow" },
  { to: "/orders", label: "Orders", icon: Receipt, testid: "nav-orders" },
  { to: "/business-info", label: "Business Info", icon: Building2, testid: "nav-business-info" },
  { to: "/documents", label: "Documents", icon: FileText, testid: "nav-documents" },
  { to: "/messages", label: "Messages", icon: MessageSquare, testid: "nav-messages" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

function pageTitle(pathname) {
  const item = NAV.find((n) => pathname.startsWith(n.to));
  return item ? item.label : "Dashboard";
}

export default function Layout() {
  const { user, logout } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Close the mobile drawer whenever the route changes.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const initials = (user?.name || "D U")
    .split(" ")
    .map((s) => s[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const BrandBlock = (
    <div className="h-16 flex items-center gap-2.5 px-5 border-b border-slate-200 dark:border-slate-800 shrink-0">
      <div
        className={`h-8 w-8 rounded-lg flex items-center justify-center overflow-hidden ${
          tenant.logo ? "bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700" : "bg-primary"
        }`}
        data-testid="sidebar-brand-tile"
      >
        {tenant.logo ? (
          <img
            src={tenant.logo}
            alt={tenant.workspaceName}
            className="h-full w-full object-contain"
            data-testid="sidebar-brand-logo"
          />
        ) : (
          <Square className="h-4 w-4 text-white" strokeWidth={2} fill="white" />
        )}
      </div>
      <div className="leading-tight min-w-0">
        <div className="font-display text-sm font-semibold text-slate-900 dark:text-slate-100 truncate" data-testid="sidebar-workspace-name">
          {tenant.workspaceName.replace(/\s*Portal$/i, "")}
        </div>
        <div className="text-[11px] text-slate-500 dark:text-slate-400 uppercase tracking-wider">
          Portal
        </div>
      </div>
    </div>
  );

  const NavBlock = (
    <nav className="flex-1 px-3 py-5 space-y-1 overflow-y-auto" data-testid="sidebar-nav">
      <div className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">
        Workspace
      </div>
      {NAV.map((item) => {
        const Icon = item.icon;
        return (
          <NavLink
            key={item.to}
            to={item.to}
            data-testid={item.testid}
            onClick={() => setMobileNavOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                isActive
                  ? "bg-primary/10 dark:bg-primary/15 text-primary font-semibold"
                  : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <Icon
                  className={`h-4 w-4 ${isActive ? "text-primary" : "text-slate-500 dark:text-slate-400"}`}
                  strokeWidth={1.75}
                />
                <span>{item.label}</span>
              </>
            )}
          </NavLink>
        );
      })}
    </nav>
  );

  const FooterBlock = (
    <div className="p-4 border-t border-slate-200 dark:border-slate-800">
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-4">
        <div className="text-xs font-semibold text-slate-900 dark:text-slate-100">Demo Portal</div>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">
          This is a static template — clone and customize for any business.
        </p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-slate-50 dark:bg-slate-900" data-testid="app-layout">
      {/* Desktop sidebar */}
      <aside
        className="hidden md:flex md:w-64 shrink-0 flex-col border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900"
        data-testid="sidebar"
      >
        {BrandBlock}
        {NavBlock}
        {FooterBlock}
      </aside>

      {/* Mobile drawer */}
      <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <SheetContent
          side="left"
          className="p-0 w-72 flex flex-col border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900"
          data-testid="mobile-sidebar"
        >
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <SheetDescription className="sr-only">
            Access every page of the portal.
          </SheetDescription>
          {BrandBlock}
          {NavBlock}
          {FooterBlock}
        </SheetContent>
      </Sheet>

      {/* Main area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header
          className="sticky top-0 z-40 h-16 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/75 dark:bg-slate-900/75 backdrop-blur-xl"
          data-testid="topbar"
        >
          <div className="h-full flex items-center gap-2 md:gap-4 px-3 md:px-8">
            <Button
              variant="ghost"
              size="icon"
              aria-label="Open navigation"
              data-testid="mobile-nav-trigger"
              onClick={() => setMobileNavOpen(true)}
              className="md:hidden rounded-lg h-10 w-10 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 shrink-0"
            >
              <Menu className="h-5 w-5" strokeWidth={1.75} />
            </Button>

            <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1 md:flex-initial">
              <h1 className="font-display text-base md:text-lg font-semibold text-slate-900 dark:text-slate-100 truncate">
                {pageTitle(location.pathname)}
              </h1>
              <Badge
                variant="outline"
                className="hidden md:inline-flex text-[10px] tracking-wider uppercase text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-800"
                data-testid="demo-badge"
              >
                Demo
              </Badge>
            </div>

            <div className="ml-auto flex items-center gap-2 md:gap-3">
              <div className="relative hidden sm:block">
                <Search className="h-4 w-4 text-slate-400 dark:text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  data-testid="topbar-search"
                  placeholder="Search..."
                  className="pl-9 w-56 h-9 bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800 rounded-lg"
                />
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                aria-label="Toggle theme"
                className="rounded-lg text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white dark:text-slate-100 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 dark:bg-slate-800 dark:hover:bg-slate-800 relative overflow-hidden"
                data-testid="theme-toggle"
              >
                <Sun
                  className={`h-4 w-4 absolute transition-all duration-500 ${
                    theme === "dark"
                      ? "rotate-90 scale-0 opacity-0"
                      : "rotate-0 scale-100 opacity-100"
                  }`}
                  strokeWidth={1.75}
                />
                <Moon
                  className={`h-4 w-4 absolute transition-all duration-500 ${
                    theme === "dark"
                      ? "rotate-0 scale-100 opacity-100"
                      : "-rotate-90 scale-0 opacity-0"
                  }`}
                  strokeWidth={1.75}
                />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="rounded-lg text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white dark:text-slate-100 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 dark:bg-slate-800 dark:hover:bg-slate-800 relative"
                data-testid="topbar-notifications"
              >
                <Bell className="h-4 w-4" strokeWidth={1.75} />
                <span className="absolute top-2 right-2 h-1.5 w-1.5 rounded-full bg-primary pulse-dot" />
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="user-menu-trigger"
                    className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 dark:bg-slate-800 transition-colors"
                  >
                    <Avatar className="h-8 w-8 border border-slate-200 dark:border-slate-800">
                      <AvatarFallback className="bg-primary text-primary-foreground text-xs font-medium">
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden sm:block text-sm text-slate-700 dark:text-slate-300 font-medium">
                      {user?.name || "Demo User"}
                    </span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col">
                      <span className="text-sm font-medium">{user?.name}</span>
                      <span className="text-xs text-slate-500 dark:text-slate-400">{user?.email}</span>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    data-testid="menu-settings"
                    onClick={() => navigate("/settings")}
                  >
                    <SettingsIcon className="h-4 w-4 mr-2" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    data-testid="menu-logout"
                    onClick={handleLogout}
                    className="text-red-600 dark:text-red-400 focus:text-red-600 dark:text-red-400"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Main content */}
        <main className="flex-1 overflow-auto" data-testid="main-content">
          <div
            key={location.pathname}
            className="max-w-7xl mx-auto px-4 md:px-8 py-8 space-y-8 page-enter"
          >
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
